// ABOUTME: Today's local date, yyyy-MM-dd, read as an external store: it moves on past local midnight, and when the tab
// ABOUTME: comes back on a later day — a screen left open overnight must not keep counting from yesterday.
import { useSyncExternalStore } from "react"
import { addDays, format, startOfDay } from "date-fns"
import { watchDayBoundary, type DayClock } from "./dayBoundary"

const localDayOf = (now: number): string => format(now, "yyyy-MM-dd")

const LOCAL_CLOCK: DayClock = {
  dayOf: localDayOf,
  // Calendar arithmetic, not 24 hours: a day with a clock change lasts 23 or 25. Where the change falls at midnight,
  // today began at 01:00 — the next day's start is taken from the next day itself, not from today's.
  untilNextMidnight: (now) => startOfDay(addDays(now, 1)).getTime() - now,
  // So that the date read when the timer fires is already the next one.
  marginMs: 1000,
}

/** Every open tab of a timezone crosses its midnight at once, and a new day re-reads what is keyed on it. */
export const LOCAL_MIDNIGHT_JITTER_MS = 30_000

const subscribe = (onDayChange: () => void) => watchDayBoundary(LOCAL_CLOCK, onDayChange, LOCAL_MIDNIGHT_JITTER_MS)
const readLocalDay = () => localDayOf(Date.now())

/**
 * A store, not state read once: `new Date()` in render keeps whatever day the last render saw, and nothing re-renders
 * at midnight. React also reads the store again once it has subscribed (updateStoreInstance → checkIfSnapshotChanged,
 * react-dom-client.development.js:8238-8242), so a midnight between the render and the watch is not lost.
 */
export function useLocalDay(): string {
  return useSyncExternalStore(subscribe, readLocalDay)
}
